import { useEffect } from 'react';
import { clarityPlanState, identifyClarityUser, setClarityTag } from '../lib/clarity.js';
import { useSession } from './SessionProvider.js';

/**
 * Labels the Clarity recording with who it is, in terms that are safe to hand to a third party:
 * the person's id, the company slug and its plan state. Nothing from the profile.
 *
 * A session someone else is acting in (a colleague impersonating, a platform admin who entered the
 * organisation) is tagged `impersonated` so those recordings can be filtered out of the product
 * numbers; they are not identified as the person they borrowed.
 */
export function useClarityTags(): void {
  const session = useSession();
  const me = session.status === 'authenticated' ? session.me : null;

  const personId = me?.person.id;
  const orgSlug = me?.person.orgSlug;
  const planState = me ? clarityPlanState(me) : null;
  const impersonated = me
    ? me.session.impersonatedBy !== null || me.session.impersonatedByPlatformAdmin
    : false;

  useEffect(() => {
    if (!personId || !orgSlug || !planState) return;
    setClarityTag('company', orgSlug);
    setClarityTag('plan_state', planState);
    setClarityTag('impersonated', impersonated ? 'yes' : 'no');
    if (!impersonated) identifyClarityUser(personId);
  }, [personId, orgSlug, planState, impersonated]);
}
